import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, shallowRef, triggerRef, watch, reactive } from 'vue'
import {
  RapooDeviceManager,
  BaseDevice,
  ConnectionType,
  ReportEvents,
  BaseDockEvents,
  DeviceEvents,
  isTransportDeviceError,
  type Role,
  type PairingContext,
} from '@rapoo/device-sdk'
import type { ColorSeriesItem, DeviceInterfaceType } from '@rapoo/device-sdk'
import { loadManifests } from '@/manifests'
import { toast } from '@rapoo/element/components/ui/sonner'
import { getRapooDeviceManagerOptions } from '@/utils/oss'
import { useLocaleStore } from './locale'
import { useRoute, useRouter } from 'vue-router'
import { useFirmwareStore } from './firmware'
import { useLoading } from '@/composables/useLoading'
import { useUserStore } from './user'
import { useAdminMode } from '@/composables/useAdminOnly/adminMode'
import { useDevicePreferencesStore, type DeviceIdentifier } from './devicePreferences'
import { runFirmwareUpdateForDevice } from '@/composables/useFirmwareUpgrade/runFirmwareUpdate'
import { bindBootAmbiguousHandler } from '@/composables/bindBootAmbiguousHandler'
import { ensureKeyboardPerfHostHooks } from '@/composables/registerKeyboardPerfHostHooks'
import { shouldLeaveDeviceRoute } from '@/router/deviceNavigationPolicy'

export type DeviceItem = {
  name: string
  img: string
  battery: number
  icon: string
  id: string
  connectionType: ConnectionType
  original: BaseDevice
  overview?: boolean
  batteryStatus: number
  connectIcon: string
  type: DeviceInterfaceType
}

/** 设备实时上报数据快照，key 为 device.id，需要用到其他数据再添加 */
type DeviceReportSnapshot = {
  batPercent: number
  batStatus: number
}

export const useDeviceStore = defineStore('device', () => {
  let manager: RapooDeviceManager | null = null

  const originDeviceList = shallowRef<BaseDevice[]>([]) // 原始设备列表，未去重，未分辨是否激活
  const deviceList = shallowRef<BaseDevice[]>([]) // 已激活的设备列表（可供 devices 直接过滤展示使用的列表）
  const currentDevice = shallowRef<BaseDevice | null>(null)
  const isScanning = ref(false)
  const devicePreferencesStore = useDevicePreferencesStore()

  function restoreDeviceColor(device: BaseDevice) {
    const colors = device.manifest.ui.colors
    if (!colors) return

    const deviceIdentifier: DeviceIdentifier = {
      vid: device.manifest.hardware.vendorId,
      pid: device.manifest.hardware.productId,
    }
    const colorKey = devicePreferencesStore.getColor(deviceIdentifier)
    if (!colorKey) return

    if (colors.series.some((color) => color.key === colorKey)) {
      colors.current = colorKey
    } else {
      devicePreferencesStore.removeColor(deviceIdentifier)
    }
  }

  function persistDeviceColor(device: BaseDevice, colorKey: string) {
    devicePreferencesStore.setColor(
      {
        vid: device.manifest.hardware.vendorId,
        pid: device.manifest.hardware.productId,
      },
      colorKey,
    )
  }

  /** 设备实时上报数据缓存（reactive Map，key 为 device.id） */
  const deviceReportMap = reactive(new Map<string, DeviceReportSnapshot>())

  /** 设备上报事件清理函数表 key => cleanupFn，设备断开时调用 */
  const deviceReportCleanups = new Map<string, () => void>()

  /** 底座子设备在线状态表，key 为子设备 id，value 为是否在线。
   *  断线时 subDevices 中仍保留子设备实例（只标记 offline），
   *  realItem 需据此决定是否切换到子设备展示。 */
  const childDeviceOnlineMap = reactive(new Map<string, boolean>())

  /**
   * 暂存子设备：当用户在浏览父设备页面时，子设备上线不会立即切换 currentDevice，
   * 而是将子设备暂存在此。当用户离开父设备页面时，再切换到此子设备。
   * 若有多个子设备先后上线，只保留第一个。
   * 若为 null，则无暂存的切换需求。
   */
  const pendingChildDevice = shallowRef<BaseDevice | null>(null)

  /** 传输失败 toast 去抖，避免同一次休眠触发的并发请求连环弹窗 */
  let lastUnreachableToastAt = 0
  const UNREACHABLE_TOAST_COOLDOWN_MS = 3000

  const router = useRouter()
  const route = useRoute()

  /** 标记当前是否正在执行用户主动的设备切换导航，期间 watch 不重复触发 router.replace */
  let isUserNavigating = false

  const isConnected = computed(() => !!currentDevice.value)
  const { t } = useLocaleStore()
  const { isLoading, withLoading } = useLoading()

  function isDeviceRelatedToCurrent(device: BaseDevice): boolean {
    const cur = currentDevice.value
    if (!cur) return false
    if (cur === device || cur.id === device.id) return true
    if (cur.parentDevice === device || cur.parentDevice?.id === device.id) return true
    if (device.parentDevice === cur || device.parentDevice?.id === cur.id) return true
    if (cur.subDevices?.some((child) => child.id === device.id)) return true
    return false
  }

  /** 设备 IO 传输失败时提示（与首页进入失败同一文案） */
  function notifyDeviceUnreachable() {
    // 首页进入已有 checkConnect / overview 的提示，避免重复弹窗
    if (route.path === '/' || route.name === 'home') return

    const firmwareStore = useFirmwareStore()
    if (firmwareStore.isFirmwareUpdating) return

    const now = Date.now()
    if (now - lastUnreachableToastAt < UNREACHABLE_TOAST_COOLDOWN_MS) return
    lastUnreachableToastAt = now
    toast.error(t('toast.device.connectedFailed2'))
  }

  function handleDeviceIoFailed(device: BaseDevice, error: unknown) {
    if (!isTransportDeviceError(error)) return
    if (!isDeviceRelatedToCurrent(device)) return
    notifyDeviceUnreachable()
  }
  /**
   * 初始化设备管理器
   * 应该在 App 启动时调用一次。
   * manifests 在此异步加载，避免与 SDK 在首屏模块图里并行抢请求队列。
   */
  async function init() {
    if (manager) return

    try {
      const manifests = await loadManifests()
      console.log('[DeviceStore] Initializing RapooDeviceManager with manifests:', manifests.length)

      // 获取单例，统一注入渠道相关的固件下载源与版本资格策略。
      manager = RapooDeviceManager.getInstance(manifests, getRapooDeviceManagerOptions())

      // 绑定事件监听
      manager.on('device:connected', handleDeviceConnected)
      manager.on('device:disconnected', handleDeviceDisconnected)
      // Boot 多型号无归属时弹窗选择（刷新后无 APP 前置记录）
      bindBootAmbiguousHandler(manager)
      // GCOME：进入设备 checkConnect 时若 USB2+已装16K，由 SDK 钩子弹提示
      ensureKeyboardPerfHostHooks()

      // 尝试恢复已授权设备连接
      void manager.restore()

      // 仅开发环境：暴露 SDK 缓存调试工具到 window
      installCacheDevTools()

      console.log('[DeviceStore] Initialized successfully')
    } catch (error) {
      console.error('[DeviceStore] Initialization failed:', error)
      toast.error(t('toast.device.initializeFailed'))
    }
  }

  /**
   * 启动设备扫描
   */
  async function scan() {
    if (!manager) {
      console.warn('[DeviceStore] Manager not initialized, calling init() first')
      await init()
    }

    if (isScanning.value) return

    try {
      isScanning.value = true
      console.log('[DeviceStore] Starting scan...')
      await manager?.scan()
    } catch (error) {
      console.error('[DeviceStore] Scan failed:', error)
      toast.error(t('toast.device.scanFailed'))
    } finally {
      isScanning.value = false
    }
  }

  /**
   * 处理设备连接事件
   */
  async function handleDeviceConnected(device: BaseDevice) {
    console.log(
      '[DeviceStore] Device connected:',
      device.manifest.model,
      device.isFirmwareUpdateMode() ? '(update mode)' : '',
    )

    restoreDeviceColor(device)

    if (manager) {
      originDeviceList.value = manager.getDevices()
    }

    // 检查是否已存在（避免重复添加）
    // 这里跟软件端一致，直接使用设备名作为唯一标识
    // 因为出于各种因素考虑，后续可能会有两个不同主控、摸具、固件的设备，但是设备名是相同的，因为要被识别为同一个控件
    const index = deviceList.value.findIndex((d) => d.manifest.model === device.manifest.model)

    // 当前设备是否切换连接状态
    let currentDeviceChangeConnect = false
    let addedToDisplayList = false

    if (device.isFirmwareUpdateMode()) {
      // Boot/update：同型号优先展示 update，替换已有 APP
      if (index === -1) {
        deviceList.value = [...deviceList.value, device]
        addedToDisplayList = true
      } else {
        const existingDevice = deviceList.value[index]
        currentDeviceChangeConnect =
          currentDevice.value?.manifest.model === existingDevice.manifest.model ||
          currentDevice.value?.id === existingDevice.id
        const nextList = [...deviceList.value]
        nextList.splice(index, 1, device)
        deviceList.value = nextList
        addedToDisplayList = true
      }
    } else if (index === -1) {
      // APP：列表无同型号 → 添加
      deviceList.value = [...deviceList.value, device]
      addedToDisplayList = true
    } else {
      const existingDevice = deviceList.value[index]

      // 已有 update 展示时，APP 只进 origin，不进展示列表
      if (existingDevice.isFirmwareUpdateMode()) {
        addedToDisplayList = false
      }
      // —— 规则1：vid、currentPid、连接类型完全相同，断线重连，直接替换 ——
      else {
        const exactVid =
          existingDevice.manifest.hardware.vendorId === device.manifest.hardware.vendorId
        const exactPid =
          existingDevice.manifest.hardware.currentPid === device.manifest.hardware.currentPid
        const exactConnection = existingDevice.getConnectionType() === device.getConnectionType()

        if (exactVid && exactPid && exactConnection) {
          currentDeviceChangeConnect =
            currentDevice.value?.manifest.model === existingDevice.manifest.model
          const nextList = [...deviceList.value]
          nextList.splice(index, 1, device)
          deviceList.value = nextList
          addedToDisplayList = true
        }
        // —— 规则2：vid 相同，新设备 currentPid 在旧设备 pids 列表中，说明同一设备切换连接模式 ——
        // 新设备是无线 → 忽略（保留有线优先）
        // 新设备是有线 → 替换旧设备
        else if (
          exactVid &&
          getProductIds(existingDevice).includes(device.manifest.hardware.currentPid)
        ) {
          const isWireless = device.getConnectionType() === ConnectionType.Wireless
          if (!isWireless) {
            currentDeviceChangeConnect =
              currentDevice.value?.manifest.model === existingDevice.manifest.model
            const nextList = [...deviceList.value]
            nextList.splice(index, 1, device)
            deviceList.value = nextList
            addedToDisplayList = true
          }
        }
      }
    }

    // 自动连接并设为当前设备（策略可调整）
    // 固件升级锁定期间勿自动切选中设备（进 Boot 断连后另一台连上也不抢占）
    const firmwareStore = useFirmwareStore()
    const upgradeLocked = firmwareStore.isFirmwareUpdating && Boolean(firmwareStore.upgradingDevice)
    const isNoCurrentDevice = !currentDevice.value
    const shouldSelectUpdate = addedToDisplayList && device.isFirmwareUpdateMode() && !upgradeLocked
    if (
      !upgradeLocked &&
      addedToDisplayList &&
      (isNoCurrentDevice || currentDeviceChangeConnect || shouldSelectUpdate)
    ) {
      void selectDevice(device)
    }

    // 订阅该设备的实时上报数据
    subscribeDeviceReport(device)

    // 如果该设备是底座，订阅子设备列表变更事件（subDevices push/splice 无法被 shallowRef 感知）
    subscribeBaseChildEvents(device)

    // 设备连接时根据当前用户的真实 roleIds 设置 SDK 配置角色
    const userStore = useUserStore()
    const slotRoles = (userStore.userInfo?.roleIds as Role[]) ?? []
    device.setConfigSlotRoles(slotRoles)
    // 同时设置所有子设备的配置角色
    device.subDevices?.forEach((sub) => sub.setConfigSlotRoles(slotRoles))
    if (userStore.isAdmin && !device.isFirmwareUpdateMode()) {
      const { enterAdminMode } = useAdminMode()
      enterAdminMode(device)
    }
    // Boot 模式无法读 APP 版本，跳过
    if (!device.isFirmwareUpdateMode() && !isPairingMode(device)) {
      firmwareStore.checkLatestVersion(device)
    }
  }

  /**
   * 处理设备断开事件
   */
  async function handleDeviceDisconnected(device: BaseDevice) {
    console.log('[DeviceStore] Device disconnected:', device.manifest.model)

    if (manager) {
      originDeviceList.value = manager.getDevices()
    }

    const firmwareStore = useFirmwareStore()
    const upgradeLocked =
      firmwareStore.isFirmwareUpdating && firmwareStore.isUpgradingDevice(device)

    const wasUpdateMode = device.isFirmwareUpdateMode()
    const model = device.manifest.model
    const index = deviceList.value.findIndex((d) => d.manifest.model === model)
    const wasInDisplayList = index > -1 && deviceList.value[index]?.id === device.id

    if (wasInDisplayList) {
      const nextList = [...deviceList.value]
      nextList.splice(index, 1)
      deviceList.value = nextList

      // update 离线后：若同型号 APP 仍在 origin 且在线，重新展示 APP
      if (wasUpdateMode && !upgradeLocked) {
        const appDevice = originDeviceList.value.find(
          (d) =>
            d.manifest.model === model &&
            !d.isFirmwareUpdateMode() &&
            d.channel?.isConnected &&
            d.id !== device.id,
        )
        if (appDevice) {
          handleDeviceConnected(appDevice)
        }
      }

      // 升级锁定期间不要因有线/无线备选把当前设备切走。
      // 接收器配对时允许在有线源设备拔出后恢复已在线的无线接收器；
      // 底座配对仍由 SDK 的子设备刷新流程处理，不走这里的备选设备切换。
      const pairingContext = manager?.getPairingContext()
      const canRestoreAlternative = !isPairingMode(device) || pairingContext?.kind === 'receiver'
      if (!upgradeLocked && !wasUpdateMode && canRestoreAlternative) {
        const devicePids = getProductIds(device)
        if (devicePids.length > 1) {
          const connectionType = device.getConnectionType()
          const targetConnectionType =
            connectionType === ConnectionType.Wired ? ConnectionType.Wireless : ConnectionType.Wired
          const alternativeDevice = originDeviceList.value.find(
            (d) =>
              d.getConnectionType() === targetConnectionType &&
              getProductIds(d).includes(device.manifest.hardware.currentPid),
          )
          if (alternativeDevice) {
            handleDeviceConnected(alternativeDevice)
          }
        }
      }
    }

    // 清理该设备的固件状态（升级中的目标机保留 versions 亦可，断连后 id 仍有用；与原逻辑一致删除）
    const { versionsMap } = storeToRefs(firmwareStore)
    versionsMap.value.delete(device.id)

    // 取消订阅该设备的上报事件，并清理缓存
    unsubscribeDeviceReport(device)
    unsubscribeBaseChildEvents(device)

    // 如果当前设备断开：升级锁定则保持选中（全屏进度仍指向该机）；否则切下一台
    if (currentDevice.value?.id === device.id) {
      if (upgradeLocked) {
        return
      }
      // 设备配置页断连：清空选中并回首页（回报率切换等会短暂断连重枚举）；
      // 首页断连：切到列表中下一台在线设备，便于双设备场景展示切换。
      const onDevicePage = route.path.startsWith('/device/')
      currentDevice.value = onDevicePage ? null : (deviceList.value[0] ?? null)
      if (!isPairingMode(device) && !currentDevice.value && deviceList.value.length === 0) {
        // #if (__CHANNEL__ === 'china_cybercafe')
        toast.error(t('toast.device.disconnected2'))
        // #endif
        // #if (__CHANNEL__ !== 'china_cybercafe')
        toast.error(t('toast.device.disconnected'))
        // #endif
      }
    }
  }

  function getProductIds(dev: BaseDevice) {
    const pid = dev.manifest.hardware.productId
    return Array.isArray(pid) ? pid : [pid]
  }

  /**
   * 仅开发环境：把 SDK 缓存调试工具挂到 window.__rapooCache。
   *
   * 用法（浏览器 devtools 控制台）：
   *   __rapooCache.debug()         // 开启 HIT/MISS 日志（console.debug，需在控制台勾选 Verbose 级别）
   *   __rapooCache.shadow()        // 开启 Shadow Read：每次命中偷读硬件比对，不一致直接抛错
   *   __rapooCache.shadow(false)   // 关闭（开启时每次命中都会翻倍读硬件，测完务必关掉）
   *   __rapooCache.size()          // 当前设备缓存条目数
   *   __rapooCache.clear()         // 清空当前设备缓存
   *   __rapooCache.device()        // 取当前选中的设备实例
   *
   * 注意：所有操作都作用于「当前选中设备」(currentDevice)，切换设备后自动跟随。
   */
  function installCacheDevTools() {
    if (!import.meta.env.DEV) return
    const w = window as unknown as Record<string, unknown>
    if (w.__rapooCache) return

    const requireDevice = (): BaseDevice | null => {
      const dev = currentDevice.value
      if (!dev) console.warn('[rapooCache] 当前没有选中设备')
      return dev
    }

    w.__rapooCache = {
      device: () => currentDevice.value,
      debug: (on = true) => {
        ;(globalThis as Record<string, unknown>).__RAPOO_SDK_CACHE_DEBUG = on
        console.log(
          `[rapooCache] HIT/MISS 日志已${on ? '开启' : '关闭'}` +
            (on ? '（console.debug 输出，需在控制台日志级别中勾选 Verbose）' : ''),
        )
      },
      shadow: (on = true) => {
        const dev = requireDevice()
        if (!dev) return
        dev.cache.__enableShadowRead(on)
        console.log(
          `[rapooCache] Shadow Read 已${on ? '开启' : '关闭'}` +
            (on ? '（每次命中都会翻倍读硬件并比对，测完记得 shadow(false)）' : ''),
        )
      },
      size: () => {
        const dev = requireDevice()
        return dev ? dev.cache.size() : 0
      },
      clear: () => {
        const dev = requireDevice()
        dev?.cache.clearAll()
        console.log('[rapooCache] 当前设备缓存已清空')
      },
      enabled: () => {
        const dev = requireDevice()
        dev?.cache.enable()
        console.log('[rapooCache] 当前设备缓存已启用')
      },
      disabled: () => {
        const dev = requireDevice()
        dev?.cache.disable()
        console.log('[rapooCache] 当前设备缓存已禁用')
      },
    }

    console.log(
      '[rapooCache] 调试工具已就绪：__rapooCache.debug() / .shadow() / .size() / .clear() / .device()',
    )
  }

  /**
   * 订阅设备的实时上报与传输失败事件
   */
  function subscribeDeviceReport(device: BaseDevice) {
    // 先清理旧的订阅（替换设备场景）
    unsubscribeDeviceReport(device)

    const onBatteryChanged = (data: { batPercent: number; batStatus: number }) => {
      deviceReportMap.set(device.id, {
        batPercent: data.batPercent,
        batStatus: data.batStatus,
      })
    }

    const onIoFailed = (error: unknown) => {
      handleDeviceIoFailed(device, error)
    }

    device.on(ReportEvents.Battery, onBatteryChanged)
    device.on(DeviceEvents.IoFailed, onIoFailed)

    // 保存清理函数
    deviceReportCleanups.set(device.id, () => {
      device.off(ReportEvents.Battery, onBatteryChanged)
      device.off(DeviceEvents.IoFailed, onIoFailed)
    })
  }

  /**
   * 订阅底座子设备相关事件。
   * 由于 deviceList 为 shallowRef，subDevices 的 push/splice 无法被 Vue 响应系统感知，
   * 因此需要通过 SDK 事件手动触发 triggerRef(deviceList)，驱动 devices computed 重新求值。
   *
   * 同时监听 ChildOnlineChanged 追踪子设备在线态：
   * 断线时 SDK 不从 subDevices 中移除子设备（仅标记 offline），realItem 需据此判断是否展示子设备。
   *
   * 注意：底座子鼠标是 SDK 内部创建的逻辑设备（挂在 subDevices 上，不经过
   * manager 的 device:connected 广播），因此必须在这里手动为其订阅实时上报
   * （ReportEvents.Battery），否则 B0 上报的电量/充电状态无人消费，
   * devices 里的 battery/batteryStatus 会一直停留在初始值。
   */
  function subscribeBaseChildEvents(device: BaseDevice) {
    // 仅底座设备有子设备
    if (!device.manifest?.type?.includes('base')) return

    // 补订阅：底座连接时 subDevices 若已存在在线子设备（异常残留/时序差异），
    // 不会再有 ChildOnlineChanged 事件，需主动为其订阅上报。
    device.subDevices?.forEach((child) => {
      if (childDeviceOnlineMap.get(child.id)) {
        restoreDeviceColor(child)
        subscribeDeviceReport(child)
      }
    })

    // subDevices 数组内容变更（add/remove）时刷新展示列表
    const onChildDisplayChanged = () => {
      triggerRef(deviceList)
    }
    device.on(BaseDockEvents.ChildDisplayChanged, onChildDisplayChanged)

    // 子设备上线时订阅其上报（Battery 等），下线时取消订阅并清理上报缓存
    const syncChildReportSubscription = (child: BaseDevice, online: boolean) => {
      if (online) {
        restoreDeviceColor(child)
        subscribeDeviceReport(child)
      } else {
        unsubscribeDeviceReport(child)
      }
    }

    // 子设备在线态变化时更新在线状态表并刷新展示列表
    const onChildOnlineChanged = (payload: { device: BaseDevice; online: boolean }) => {
      childDeviceOnlineMap.set(payload.device.id, payload.online)
      // 子设备上下线时同步上报订阅，使电量/充电状态能实时写入 deviceReportMap
      syncChildReportSubscription(payload.device, payload.online)
      // 子设备上线时同步当前用户的配置角色
      if (payload.online) {
        const userStore = useUserStore()
        payload.device.setConfigSlotRoles((userStore.userInfo?.roleIds as Role[]) ?? [])
      }

      if (currentDevice.value === device) {
        // currentDevice 是底座本身
        if (payload.online) {
          // 子设备上线——检查当前是否正在浏览底座（base）页面
          if (route.meta.type === 'base') {
            // 正在浏览底座页面，不切换 currentDevice，暂存子设备
            if (!pendingChildDevice.value) {
              pendingChildDevice.value = payload.device
            }
          } else {
            // 不在浏览底座页面，立即切换到子设备
            void selectDevice(payload.device)
          }
        } else {
          // 子设备下线，currentDevice 是底座，无需切换
        }
      } else if (currentDevice.value === payload.device) {
        // currentDevice 正是这个下线的子设备本身，切换回底座
        void selectDevice(device)
      }

      // 暂存的子设备下线了 → 清除，尝试用其他在线子设备兜底
      if (pendingChildDevice.value === payload.device && !payload.online) {
        pendingChildDevice.value = getFirstOnlineSubDevice(device) ?? null
      }

      triggerRef(deviceList)
    }
    device.on(BaseDockEvents.ChildOnlineChanged, onChildOnlineChanged)

    // 子设备被移除/换绑（可能不经过 offline 直接 detach）时，清理上报订阅与缓存
    const onChildDeviceDisconnected = (child: BaseDevice) => {
      unsubscribeDeviceReport(child)
    }
    device.on(BaseDockEvents.ChildDeviceDisconnected, onChildDeviceDisconnected)

    const cleanupKey = `${device.id}:base-child`
    deviceReportCleanups.set(cleanupKey, () => {
      device.off(BaseDockEvents.ChildDisplayChanged, onChildDisplayChanged)
      device.off(BaseDockEvents.ChildOnlineChanged, onChildOnlineChanged)
      device.off(BaseDockEvents.ChildDeviceDisconnected, onChildDeviceDisconnected)
    })
  }

  /**
   * 取消订阅底座子设备相关事件
   */
  function unsubscribeBaseChildEvents(device: BaseDevice) {
    const cleanupKey = `${device.id}:base-child`
    const cleanup = deviceReportCleanups.get(cleanupKey)
    if (cleanup) {
      cleanup()
      deviceReportCleanups.delete(cleanupKey)
    }
  }

  /**
   * 取消订阅设备的上报事件并清理缓存
   */
  function unsubscribeDeviceReport(device: BaseDevice) {
    const cleanup = deviceReportCleanups.get(device.id)
    if (cleanup) {
      cleanup()
      deviceReportCleanups.delete(device.id)
    }
    deviceReportMap.delete(device.id)
  }

  /**
   * 获取当前驱动所有指定model的设备
   */
  function getDevices(model?: string) {
    if (!manager) {
      console.warn('[DeviceStore] Manager not initialized')
      return []
    }

    originDeviceList.value = manager.getDevices(model)
  }

  /**
   * 选择当前操作的设备
   */
  async function selectDevice(device: BaseDevice) {
    try {
      // 如果还没打开，先打开
      // 注意：BaseDevice.open() 应该是幂等的，或者检查 isOpen 状态
      // 这里假设 SDK 处理好了 open 状态
      await device.open()
      currentDevice.value = device
      console.log('[DeviceStore] Selected device:', device.manifest.model)
    } catch (error) {
      console.error('[DeviceStore] Failed to open device:', error)
      toast.error(`${t('toast.device.connectedFailed')}: ${device.manifest.model}`)
    }
  }

  /**
   * 变更设备颜色
   */
  function changeDeviceManifestCurrentColor(key: string) {
    if (!currentDevice.value?.manifest?.ui.colors) return

    currentDevice.value.manifest.ui.colors.current = key
    persistDeviceColor(currentDevice.value, key)
    triggerRef(currentDevice)
    triggerRef(deviceList)
  }

  /**
   * 获取设备当前颜色对应的 series 项
   * 按 key 匹配，未匹配到时回退到 series[0]
   */
  function getDeviceManifestCurrentColor(device: BaseDevice | null): ColorSeriesItem | undefined {
    if (!device?.manifest?.ui.colors) return

    const { series, current } = device.manifest.ui.colors
    if (!series.length) return

    return series.find((color) => color.key === current) ?? series[0]
  }

  /**
   * 设备展示列表（精简模式）。
   * 对于底座设备，如果存在在线的子设备，则直接展示子设备而不是底座本身。
   * 子设备断线时 SDK 不从 subDevices 中移除实例，由 childDeviceOnlineMap 追踪在线态。
   */
  const devices = computed(() =>
    deviceList.value.map((device) => {
      const onlineSub = getFirstOnlineSubDevice(device)
      return onlineSub ? baseDeviceToDeviceItem(onlineSub) : baseDeviceToDeviceItem(device)
    }),
  )

  /**
   * 设备展示列表（展开模式）。
   * 对于底座设备，将其所有在线的子设备展开为独立的展示项（底座本身也保留），
   * 而非像 devices 那样用子设备替换底座。
   * 底座始终保持在列表首位，子设备紧随其后。
   */
  const expandedDevices = computed(() => {
    const result: DeviceItem[] = []
    for (const device of deviceList.value) {
      if (device.manifest?.type?.includes('base')) {
        // 底座始终显示
        result.push(baseDeviceToDeviceItem(device))
        // 在线的子设备展开为独立项
        const onlineChildren = device.subDevices?.filter((child) =>
          childDeviceOnlineMap.get(child.id),
        )
        if (onlineChildren?.length) {
          for (const child of onlineChildren) {
            result.push(baseDeviceToDeviceItem(child))
          }
        }
      } else {
        result.push(baseDeviceToDeviceItem(device))
      }
    }
    return result
  })

  // 设备对象转为 DeviceItem 对象，用于前端展示
  const baseDeviceToDeviceItem = (device: BaseDevice): DeviceItem => {
    // 从上报缓存读取，回退到设备对象自身的 userCustomData
    const reportSnapshot = deviceReportMap.get(device.id)
    const batPercent = reportSnapshot?.batPercent ?? device.userCustomData?.batPercent ?? 0
    const batStatus = reportSnapshot?.batStatus ?? device.userCustomData?.batStatus ?? 0
    return {
      name: device.manifest.model,
      img: (() => {
        const ui = device.manifest.ui
        const currentColor = getDeviceManifestCurrentColor(device)
        return `${ui.assetsDir}/thumb_${currentColor?.key ?? ''}.png`
      })(),
      battery: batPercent,
      batteryStatus: batStatus,
      connectionType: device.getConnectionType(),
      icon: device.manifest.ui.icon,
      connectIcon: `Hardware-Wireframe-${device.getConnectionType() === ConnectionType.Wireless ? 'Wireless' : 'Wired'}`,
      original: device as BaseDevice,
      id: device.id,
      overview: device.manifest.ui.overview,
      type: device.manifest.type ?? 'other',
    }
  }

  /** 获取底座设备中第一个在线的子设备，无在线子设备时返回 undefined */
  const getFirstOnlineSubDevice = (device: BaseDevice): BaseDevice | undefined => {
    return device.subDevices?.find((child) => childDeviceOnlineMap.get(child.id))
  }

  const getDeviceType = (device: BaseDevice | null): string => {
    if (!device?.manifest) return 'unknown'

    const layoutType = String(device.manifest?.type ?? '')
    if (layoutType.includes('mouse')) return 'mouse'
    if (layoutType.includes('keyboard')) return 'keyboard'
    if (layoutType.includes('base')) return 'base'

    return 'unknown'
  }

  const handleDeviceClick = async (item: DeviceItem, endRouter?: string, canRoute = true) => {
    if (!item || !canRoute) return

    // Boot/update：不包 withLoading（整段 IAP 会卡住「加载中」），只做选中后直接恢复升级
    if (item.original.isFirmwareUpdateMode()) {
      isUserNavigating = true
      await selectDevice(item.original)
      isUserNavigating = false
      await runFirmwareUpdateForDevice(item.original, {
        router,
        failToast: t('toast.firmware.upgradeFailed'),
        upgradingToast: t('toast.firmware.upgrading'),
      })
      return
    }

    await withLoading(async () => {
      const type = getDeviceType(item.original)
      // selectDevice 会触发 currentDevice 的 watch，
      // 设置标记让 watch 知道这是用户主动操作，不重复导航
      isUserNavigating = true
      await selectDevice(item.original)
      const canPush = await item.original.checkConnect()
      if (!canPush) {
        isUserNavigating = false
        toast.error(t('toast.device.connectedFailed2'))
        return
      }

      if (endRouter) {
        router.push(`/device/${type}/${endRouter}`)
      } else {
        router.push(`/device/${type}`)
      }
      isUserNavigating = false
    })
  }

  /**
   * 判断设备切换是否发生在底座与子设备之间（parent↔child），
   * 此类切换不应触发强制导航回首页。
   */
  function isDeviceParentChildSwitch(
    oldVal: BaseDevice | null,
    newVal: BaseDevice | null,
  ): boolean {
    if (!oldVal || !newVal) return false
    // newVal 是 oldVal 的子设备（parent → child）
    if (oldVal.subDevices?.some((child) => child.id === newVal.id)) return true
    // oldVal 是 newVal 的子设备（child → parent）
    if (newVal.subDevices?.some((child) => child.id === oldVal.id)) return true
    return false
  }

  const registerCurrentDeviceWatch = () => {
    // 监听设备变化：设备专属页不能在后台换成另一台设备后继续停留，
    // 配置中心等跨设备页面则允许主动切换 currentDevice。
    watch(
      currentDevice,
      (val, oldVal) => {
        const parentChildSwitch = Boolean(oldVal && val && isDeviceParentChildSwitch(oldVal, val))
        // 底座与子设备之间的切换，不强制导航
        if (oldVal && val && parentChildSwitch) {
          // child → parent：用户从子设备切到底座
          // 仅当该子设备仍在线时，才暂存为 pendingChildDevice
          if (
            val.subDevices?.some((child) => child.id === oldVal.id) &&
            childDeviceOnlineMap.get(oldVal.id)
          ) {
            if (!pendingChildDevice.value) {
              pendingChildDevice.value = oldVal
            }
          }
        }

        const firmwareStore = useFirmwareStore()
        const { isFirmwareUpdating, upgradingDevice } = storeToRefs(firmwareStore)

        // 固件升级锁定：正在升级的设备断连时不因切设备而离开升级流程
        if (
          isFirmwareUpdating.value &&
          upgradingDevice.value &&
          oldVal?.id === upgradingDevice.value.id
        ) {
          return
        }

        if (
          shouldLeaveDeviceRoute({
            routeDeviceType: typeof route.meta.type === 'string' ? route.meta.type : undefined,
            oldDevice: oldVal ? { id: oldVal.id, type: oldVal.manifest.type } : null,
            newDevice: val ? { id: val.id, type: val.manifest.type } : null,
            isUserNavigating,
            isFirmwareUpdating: isFirmwareUpdating.value,
            isParentChildSwitch: parentChildSwitch,
            hasParentRelationChanged: false,
          })
        ) {
          router.replace('/')
        }
      },
      { immediate: true, deep: true },
    )

    // 父子归属可能在同一个逻辑子设备实例上原地挂接/解除。保留直连/2.4 切换保护，
    // 但只在设备专属页生效，避免配置中心切换“底座鼠标 ↔ 独立设备”时误退出。
    watch(
      () => {
        // attach/detach 会刷新 deviceList；取 ID 快照，避免比较同一对象的变更后引用。
        void deviceList.value
        const device = currentDevice.value
        return {
          device: device ? { id: device.id, type: device.manifest.type } : null,
          parentDeviceId: device?.parentDevice?.id,
        }
      },
      (val, oldVal) => {
        if (val.device?.id !== oldVal.device?.id) return
        if (val.parentDeviceId === oldVal.parentDeviceId) return

        const firmwareStore = useFirmwareStore()
        if (
          shouldLeaveDeviceRoute({
            routeDeviceType: typeof route.meta.type === 'string' ? route.meta.type : undefined,
            oldDevice: oldVal.device,
            newDevice: val.device,
            isUserNavigating,
            isFirmwareUpdating: firmwareStore.isFirmwareUpdating,
            isParentChildSwitch: false,
            hasParentRelationChanged: true,
          })
        ) {
          router.replace('/')
        }
      },
    )
  }

  /** 记录本次配对涉及的源设备与目标设备。 */
  function setPairingContext(context: PairingContext | null): void {
    manager?.setPairingContext(context)
  }

  /** 更新仍保留在 manager 中的配对上下文状态。 */
  function setPairingContextStatus(status: PairingContext['status']): void {
    manager?.setPairingContextStatus(status)
  }

  function isPairingMode(device: BaseDevice) {
    return manager?.isPairingDevice(device)
  }

  return {
    isLoading,
    originDeviceList,
    deviceList,
    currentDevice,
    isScanning,
    isConnected,
    pendingChildDevice,
    devices,
    expandedDevices,
    init,
    scan,
    getDevices,
    selectDevice,
    changeDeviceManifestCurrentColor,
    getDeviceManifestCurrentColor,
    baseDeviceToDeviceItem,
    getFirstOnlineSubDevice,
    getDeviceType,
    handleDeviceClick,
    registerCurrentDeviceWatch,
    setPairingContext,
    setPairingContextStatus,
  }
})
